/**
 * El proceso operativo como datos (ADR 0005, doc. 04 §4).
 * El motor interpreta esta definición; no conoce «autos» ni «elevadores», solo unidades, etapas,
 * estaciones y personas. El vocabulario de posventa vive en los datos.
 */

import type { Distribucion } from '../aleatoriedad/distribuciones';
import type { Parametro, OrigenDato } from './parametros';

/** Las cinco categorías del prompt §6.1: todo segundo de una unidad cae en exactamente una. */
export type Categoria =
  'valor-agregado' | 'administrativa' | 'traslado' | 'espera' | 'espera-cliente';

export const CATEGORIAS: readonly Categoria[] = [
  'valor-agregado',
  'administrativa',
  'traslado',
  'espera',
  'espera-cliente',
];

/** Tipos de estación que produce el layout (rol operativo de un elemento del catálogo). */
export type TipoEstacion =
  'bahia' | 'recepcion' | 'patio' | 'control' | 'lavado' | 'entrega' | 'ingreso' | 'salida';

/**
 * Tipos de bahía que se simulan (el elevador «inactivo» no se simula). Cada servicio indica en
 * cuáles se puede atender (paso 1.2; antes lo fijaba el catálogo de construcción).
 */
export type TipoBahiaActiva = 'mixto' | 'fast-lane' | 'mayor';

export const TIPOS_BAHIA_ACTIVA: readonly TipoBahiaActiva[] = ['mixto', 'fast-lane', 'mayor'];

/**
 * Roles de las personas. El responsable de control de calidad y el controlador (torre de control)
 * llegan con el motor completo (paso 1.6, ADR 0021). El control de calidad lo hace una persona
 * dedicada (Renato, 2026-11-04): el jefe de taller no participa en el flujo simulado.
 */
export type Rol = 'asesor' | 'tecnico' | 'calidad' | 'controlador';

export const ROLES: readonly Rol[] = ['asesor', 'tecnico', 'calidad', 'controlador'];

/** Letra del código visible de cada rol: A01, T01, Q01 (control de calidad), C01. */
export const LETRA_ROL: Record<Rol, string> = {
  asesor: 'A',
  tecnico: 'T',
  calidad: 'Q',
  controlador: 'C',
};

/** Puntos del espacio adonde caminan las personas (paso 1.6, avance 2). */
export type PuntoDePersonas = 'oficina' | 'almacen' | 'torre' | 'acceso-peatonal';

export const PUNTOS_DE_PERSONAS: readonly PuntoDePersonas[] = [
  'oficina',
  'almacen',
  'torre',
  'acceso-peatonal',
];

/** Código de cada punto en las distancias a pie (las estaciones usan el suyo: B01, R01…). */
export const CODIGO_PUNTO: Record<PuntoDePersonas, string> = {
  oficina: 'OF',
  almacen: 'ALM',
  torre: 'TOR',
  'acceso-peatonal': 'PEA',
};

/**
 * Recorrido a pie dentro de una actividad (doc. 04 §4.1; ADR 0021, avance 2): una persona de la
 * etapa o el cliente camina; ocurre al inicio de la actividad. Para el auto, caminar es «traslado»
 * y el tiempo en el destino es «espera».
 */
export interface Recorrido {
  /** Quién camina: una persona de la etapa (por su rol) o el cliente. */
  quien: Rol | 'cliente';
  /** Desde dónde: el lugar del auto, donde quedó la persona o un punto del espacio. */
  desde: 'auto' | 'donde-esta' | PuntoDePersonas;
  /** Adónde: el lugar del auto o un punto del espacio. */
  hacia: 'auto' | PuntoDePersonas;
  /** Vuelve al punto de partida (por ejemplo, el técnico que va al almacén). */
  idaYVuelta?: boolean;
  /** Minutos en el destino antes de volver (atención en el mostrador). */
  atencion?: Distribucion;
  /** Solo si la etapa ocurre en estos tipos de elevador (en Fast Lane el kit llega preparado). */
  soloEnBahias?: TipoBahiaActiva[];
}

/** Hitos del negocio que marca una etapa. */
export type Hito = 'apertura-ot' | 'trabajo-terminado' | 'listo' | 'fin';

/**
 * Destino especial de una ruta dentro de una interrupción: el auto vuelve al trabajo interrumpido
 * con su tiempo restante (paso 1.6, ADR 0021).
 */
export const REANUDAR = '@reanudar';

/** Mini-lenguaje cerrado de condiciones, sin código ejecutable (doc. 04 §4.2). */
export type Condicion =
  | { tipoUnidad: string }
  | { atributo: string; igual: boolean }
  | { probabilidad: number }
  | { ubicacionActual: TipoEstacion }
  | { y: Condicion[] }
  | { o: Condicion[] }
  | { no: Condicion };

export interface RequisitoPersona {
  rol: Rol;
  /**
   * Hasta cuándo se retiene la persona: al terminar esta etapa (`fin-de-etapa`, por defecto) o al
   * terminar una etapa posterior (su id), como el técnico que hace también el control de calidad.
   */
  retenerHasta?: string;
  /** Usa una persona de este rol que la unidad ya retiene (no adquiere otra). */
  deLaUnidad?: boolean;
  /**
   * Prefiere a la persona de este rol que trabajó antes en la OT, si está libre (por ejemplo, el
   * técnico original en un retrabajo; paso 1.6, avance 5).
   */
  prefiereAnterior?: boolean;
}

/**
 * Interrupción de una actividad (por ejemplo, la espera de repuesto o el adicional a mitad del
 * servicio). La etapa intercalada ocurre con el auto donde está. Desde ella, las rutas pueden seguir
 * por otras etapas intercaladas (llamada, aprobación, trabajo extra) y terminan en `REANUDAR`; una
 * etapa intercalada sin rutas también vuelve al trabajo interrumpido.
 */
export interface Interrupcion {
  si: Condicion;
  /** Fracción de la actividad (0–1) en que ocurre. */
  fraccion: Distribucion;
  /** Primera etapa intercalada (no se alcanza por las rutas del proceso). */
  etapa: string;
  /**
   * Minutos tras los que se liberan las personas detenidas, contados desde la interrupción. Sin
   * valor, se retienen hasta volver al trabajo (como el v6). Al volver, la OT las pide con prioridad
   * alta y prefiere a las originales (ajuste 2 del ADR 0013; ADR 0017 y 0021).
   */
  liberarPersonasTras?: Parametro<number>;
}

export interface DuracionEtapa {
  /** Distribución en minutos por tipo de unidad; la clave «*» vale para todos. */
  porTipo: Record<string, Distribucion>;
  origen: OrigenDato;
  nota?: string;
  /** Tiempos supuestos que había antes de editarlos («volver al supuesto»). */
  supuesto?: Record<string, Distribucion>;
  /**
   * Tiempo propio según el tipo de elevador donde ocurre la etapa, por tipo de unidad (clave «*»
   * para todos). Por ejemplo, el mantenimiento en Fast Lane (ajuste 1 del ADR 0013).
   */
  porBahia?: Partial<Record<TipoBahiaActiva, Record<string, Distribucion>>>;
}

/** Parte de una espera del cliente que decide al día siguiente (ajuste 3 del ADR 0013). */
export interface DecideAlDiaSiguiente {
  /** Fracción (0–1) de los clientes que deciden al día siguiente. */
  probabilidad: Parametro<number>;
  /** Entre qué horas del día siguiente deciden (hh:mm), con la misma probabilidad. */
  desde: string;
  hasta: string;
}

/**
 * Regreso del cliente a retirar su auto (ajuste 5 del ADR 0013): una parte vuelve cerca de su hora
 * prometida vigente y otra al final de la tarde (si la promesa es anterior), nunca antes de
 * «listo + aviso». Si cae después del límite del día, vuelve el día siguiente entre dos horas.
 */
export interface RegresoDelCliente {
  /** Fracción (0–1) que vuelve cerca de su hora prometida. */
  cercaDePromesa: Parametro<number>;
  /** Minutos respecto de la hora prometida (puede ser negativo: llega antes). */
  desviacion: Distribucion;
  /** Ventana del final de la tarde (hh:mm) para el resto. */
  finDeTarde: { desde: string; hasta: string };
  /** Minutos desde «listo» hasta que el cliente, avisado, puede llegar. */
  aviso: Distribucion;
  /** Hora límite de retiro del día (hh:mm). */
  limite: string;
  /** Ventana del día siguiente (hh:mm) si el regreso pasa del límite. */
  siguienteDia: { desde: string; hasta: string };
}

export type TipoEtapa = 'actividad' | 'espera-externa' | 'salida';

export interface Etapa {
  id: string;
  nombre: string;
  tipo: TipoEtapa;
  categoria: Categoria;
  /** Obligatoria para los tipos de `aplicaA` (o para todos si se omite). */
  obligatoria: boolean;
  aplicaA?: string[];
  /** Tipo de estación donde ocurre; `actual` = donde ya está la unidad. */
  lugar: TipoEstacion | 'actual';
  /**
   * Dónde espera la unidad si la etapa no puede empezar: `actual` (retiene y bloquea su
   * estación) o `patio` (estación de amortiguación; si no hay espacio, `actual`).
   */
  lugarDeEspera?: 'actual' | 'patio';
  personas: RequisitoPersona[];
  duracion?: DuracionEtapa;
  /** Solo espera externa: la duración corre en horas hábiles dentro de esta ventana diaria. */
  ventanaHabil?: { inicio: string; fin: string };
  /**
   * Solo espera externa en el lugar actual: las personas que el auto retiene al empezarla se
   * liberan tras estos minutos (por ejemplo, el técnico durante la aprobación del cliente).
   */
  liberarPersonasTras?: Parametro<number>;
  /** Solo espera externa: una parte de los clientes decide al día siguiente. */
  alDiaSiguiente?: DecideAlDiaSiguiente;
  /**
   * Solo espera externa con un lugar propio (por ejemplo, el patio): si no hay ninguno libre, la
   * espera empieza igual donde está el auto (el repuesto sin stock con el patio lleno se espera en el
   * elevador; paso 1.6, avance 5).
   */
  siNoHayLugarEsperaDondeEsta?: boolean;
  /** Solo espera externa: el fin de la espera es el regreso del cliente (retiro). */
  regreso?: RegresoDelCliente;
  /**
   * Reserva ahora el lugar y las personas de la etapa siguiente, todo a la vez: la torre de control
   * asigna el elevador y el técnico (doc. 04 §4.5, n.º 2). Si no puede, la etapa no empieza.
   */
  reservaSiguiente?: boolean;
  /** Recorridos a pie al inicio de la actividad (técnico al almacén, responsable de calidad…). */
  recorridos?: Recorrido[];
  /**
   * Solo actividad: hora del día (hh:mm) después de la cual la etapa ya no empieza. Puede ser antes
   * del fin del turno (corte del control a las 17:30) o después (las personas atienden en horas
   * extra, como la entrega hasta las 19:00). Sin el campo, el fin del turno (paso 1.6, avance 5).
   */
  aceptaHasta?: string;
  /**
   * La OT entra a la cola de esta etapa con prioridad alta (vuelve de un repuesto, de una aprobación
   * o de un retrabajo; paso 1.6, avance 5).
   */
  prioridadAlta?: boolean;
  interrupcion?: Interrupcion;
  /**
   * Más interrupciones de la misma actividad (por ejemplo, repuesto y adicional en el servicio). En
   * cada OT ocurre primero la que cae antes en el trabajo; la otra, al reanudar (ADR 0021).
   */
  otrasInterrupciones?: Interrupcion[];
  hitoAlIniciar?: Hito;
  hitoAlTerminar?: Hito;
}

/**
 * Efectos que ocurren cuando una OT toma una ruta (paso 1.6, ADR 0021). Cada uno es un supuesto
 * editable con su origen.
 */
export interface EfectosDeRuta {
  /** La hora prometida vigente se corre estos minutos hábiles (adicional aprobado, A5). */
  correrPromesaMin?: Parametro<number>;
  /** Horas vendidas que se suman a la OT y se cuentan al terminar su trabajo (adicional). */
  sumarHorasVendidas?: Parametro<number>;
  /** El trabajo técnico de la OT termina aquí y vende estas horas (solo diagnóstico). */
  terminarTrabajoConHoras?: Parametro<number>;
  /**
   * La hora prometida vigente pasa al día siguiente a esta hora (hh:mm), como cuando el asesor
   * avisa una nueva promesa por un repuesto sin stock; la original se conserva (paso 1.6, avance 5).
   */
  promesaDiaSiguiente?: Parametro<string>;
}

export interface Transicion {
  de: string;
  /** Etapa siguiente, o `REANUDAR` dentro de una interrupción. */
  a: string;
  si?: Condicion;
  /** Ruta por defecto si ninguna condición anterior se cumple. */
  sino?: boolean;
  /**
   * Máximo de veces que una OT toma esta ruta (retrabajo con límite, paso 1.2). Agotado, la ruta ya
   * no se toma y se evalúan las siguientes. La ruta por defecto no lleva límite.
   */
  maximo?: number;
  efectos?: EfectosDeRuta;
}

export interface TipoDeUnidad {
  id: string;
  nombre: string;
  /** Plazo de la promesa en minutos hábiles desde la apertura de la OT (tipo `habiles`). */
  promesaMin: Parametro<number>;
  /**
   * Cómo se promete (paso 1.2): `habiles` (por defecto), un plazo en horas hábiles desde la
   * apertura de la OT; `hoy`, hoy a una hora fija (si la OT se abre después de esa hora, al día
   * siguiente a la misma hora); `manana`, al día siguiente a una hora fija.
   */
  promesaTipo?: 'habiles' | 'hoy' | 'manana';
  /** Hora fija de la promesa (hh:mm) para `hoy` y `manana`. */
  promesaHora?: Parametro<string>;
  /** Horas estándar facturables del servicio. */
  horasVendidas: Parametro<number>;
  /** Tipos de bahía (elevador) donde se puede atender; un tipo especializado se prefiere. */
  bahias: TipoBahiaActiva[];
}

export interface AtributoDeUnidad {
  id: string;
  nombre: string;
  /** Probabilidad (0–1) por tipo de unidad; los tipos omitidos tienen 0. */
  probabilidad: Parametro<Record<string, number>>;
}

export interface DefinicionDeProceso {
  id: string;
  nombre: string;
  version: number;
  tiposDeUnidad: TipoDeUnidad[];
  atributos: AtributoDeUnidad[];
  etapaInicial: string;
  /** Orden declarado = orden aguas arriba → aguas abajo (la fase C lo recorre al revés). */
  etapas: Etapa[];
  transiciones: Transicion[];
}

/**
 * Distribución de una etapa para un tipo de unidad (y, si ocurre en un elevador, para su tipo), o
 * `undefined` si no está definida.
 */
export function distribucionDe(
  etapa: Etapa,
  tipoUnidad: string,
  tipoBahia?: TipoBahiaActiva,
): Distribucion | undefined {
  const propia = tipoBahia ? etapa.duracion?.porBahia?.[tipoBahia] : undefined;
  return (
    propia?.[tipoUnidad] ??
    propia?.['*'] ??
    etapa.duracion?.porTipo[tipoUnidad] ??
    etapa.duracion?.porTipo['*']
  );
}

/** Todas las interrupciones de una etapa, en el orden declarado. */
export function interrupcionesDe(e: Etapa): Interrupcion[] {
  return [...(e.interrupcion ? [e.interrupcion] : []), ...(e.otrasInterrupciones ?? [])];
}

/**
 * Etapas intercaladas de cada interrupción: la primera y las que se alcanzan desde ella por rutas,
 * hasta volver al trabajo. Clave: etapa intercalada; valor: etapas que se interrumpen hacia ella.
 */
export function etapasIntercaladas(p: DefinicionDeProceso): Map<string, Set<string>> {
  const r = new Map<string, Set<string>>();
  for (const e of p.etapas) {
    const pila = interrupcionesDe(e).map((i) => i.etapa);
    while (pila.length) {
      const id = pila.pop()!;
      if (id === REANUDAR) continue;
      const duenas = r.get(id) ?? new Set<string>();
      if (duenas.has(e.id)) continue;
      duenas.add(e.id);
      r.set(id, duenas);
      for (const t of p.transiciones) if (t.de === id) pila.push(t.a);
    }
  }
  return r;
}

/** ¿La etapa es obligatoria para este tipo de unidad? */
export function esObligatoriaPara(etapa: Etapa, tipoUnidad: string): boolean {
  return etapa.obligatoria && (etapa.aplicaA === undefined || etapa.aplicaA.includes(tipoUnidad));
}
